import React, { useRef, useState } from "react";
import { Box } from "@mui/material";
import { FIELD_TYPES, StriimButton, StriimFeedbackBlock, StriimFormBuilder, StriimTypography } from "@striim/striim-ui";
import { AskUserArgs, AskUserField, validateRequired } from "./build-a2ui-form";
import { buildCancelledResponse } from "../utils/hitl-cancel";

export interface AskUserFormProps {
    args: AskUserArgs;
    respond?: (result: string) => void;
}

// Shared container look, also reused by the confirmation variant.
export const styles = {
    container: {
        p: 2,
        border: "1px solid #D8E4F0",
        borderRadius: 2,
        mt: 1,
        backgroundColor: "#FFFFFF",
        width: "100%"
    },
    error: {
        mt: 1,
        "& > .MuiGrid-container": {
            width: "100%"
        }
    },
    actions: {
        mt: 2
    }
};

interface StriimField {
    name: string;
    type: string;
    required?: boolean;
    placeholder?: string;
    defaultValue?: unknown;
    options?: string[];
    additionalInputProps?: Record<string, unknown>;
}

/** Maps the agent-supplied field spec to StriimFormBuilder's field definitions. */
const buildStriimFields = (fields: AskUserField[]): StriimField[] =>
    fields.map(field => {
        const common = {
            name: field.name,
            required: field.required,
            placeholder: field.placeholder
        };
        switch (field.type) {
            case "longText":
                return { ...common, type: FIELD_TYPES.TEXTAREA, defaultValue: field.defaultValue ?? "" };
            case "number":
                return { ...common, type: FIELD_TYPES.NUMBER, defaultValue: field.defaultValue ?? "" };
            case "date":
                return { ...common, type: FIELD_TYPES.DATETIME, defaultValue: field.defaultValue ?? "" };
            case "boolean":
                return { ...common, type: FIELD_TYPES.BOOLEAN, defaultValue: field.defaultValue === true };
            case "choice":
                return {
                    ...common,
                    type: FIELD_TYPES.SELECT,
                    options: field.options ?? [],
                    defaultValue: field.defaultValue ?? field.options[0],
                    additionalInputProps: field.multiple ? { isMulti: true } : undefined
                };
            default:
                return { ...common, type: FIELD_TYPES.STRING, defaultValue: field.defaultValue ?? "" };
        }
    });

const optionValue = (option: unknown): unknown =>
    option && typeof option === "object" && "value" in (option as Record<string, unknown>)
        ? (option as Record<string, unknown>).value
        : option;

const normalizeValues = (fields: AskUserField[], raw: Record<string, unknown>): Record<string, unknown> => {
    const result: Record<string, unknown> = {};

    fields.forEach(field => {
        const value = raw[field.name];

        if (field.type === "boolean") {
            result[field.name] = value === true || value === "true";
        } else if (field.type === "number") {
            if (value === "" || value === null || value === undefined) {
                result[field.name] = undefined;
            } else {
                const num = typeof value === "number" ? value : Number(value);
                result[field.name] = Number.isNaN(num) ? undefined : num;
            }
        } else if (field.type === "choice") {
            if (field.multiple) {
                const arr = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
                result[field.name] = arr.map(optionValue);
            } else {
                result[field.name] = value === undefined || value === null || value === "" ? null : optionValue(value);
            }
        } else {
            result[field.name] = value ?? "";
        }
    });

    return result;
};

/**
 * Renders the generic `askUser` form. The agent supplies the field spec at runtime; we translate it
 * to StriimFormBuilder fields, collect the values on submit, and return them to the agent via
 * `respond` — `{ values }` on submit, `{ cancelled: true }` on cancel.
 */
export const AskUserForm: React.FC<AskUserFormProps> = ({ args, respond }) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [submitted, setSubmitted] = useState(false);
    const formRef = useRef<{
        values?: Record<string, unknown>;
        submitForm: () => Promise<unknown>;
        validateForm: () => Promise<Record<string, unknown>>;
    }>();
    // Synchronous re-entry guard: submit validation is async, so `submitted` (state) isn't set until
    // the promise resolves — this stops a rapid second click from firing `respond` twice.
    const submittingRef = useRef(false);

    const fields = args.fields ?? [];
    const striimFields = buildStriimFields(fields);

    const handleCancel = () => {
        if (submitted) return;
        setSubmitted(true);
        respond?.(buildCancelledResponse({ cancelled: true }));
    };

    const handleSubmit = () => {
        if (submitted || submittingRef.current || !formRef.current) return;
        submittingRef.current = true;
        const form = formRef.current;

        form.submitForm()
            .then(() => form.validateForm())
            .then(errors => {
                const invalidFields = Object.keys(errors ?? {});
                if (invalidFields.length > 0) {
                    const labels = invalidFields
                        .map(name => fields.find(field => field.name === name)?.label ?? name)
                        .join(", ");
                    setError(`Please fill in the required field(s): ${labels}`);
                    submittingRef.current = false;
                    return;
                }
                const values = normalizeValues(fields, form.values ?? {});
                setError(undefined);
                setSubmitted(true);
                respond?.(`Proceed with the following values: ${JSON.stringify(values)}`);
            })
            .catch(() => {
                submittingRef.current = false;
            });
    };

    return (
        <Box sx={styles.container}>
            {args.title && <StriimTypography variant="h3">{args.title}</StriimTypography>}
            {args.description && (
                <StriimTypography variant="body4" color="greyscale.800">
                    {args.description}
                </StriimTypography>
            )}
            {error && (
                <Box sx={styles.error} data-testid="ask-user-error">
                    <StriimFeedbackBlock variant="critical" type="compact" content={error} />
                </Box>
            )}
            <StriimFormBuilder formRef={formRef as any} data={striimFields as any} validateOnChange />
            <Box sx={styles.actions}>
                <Box mb={1}>
                    <StriimButton fullWidth variant="primary" onClick={handleSubmit}>
                        {args.submitLabel ?? "Submit"}
                    </StriimButton>
                </Box>
                <StriimButton fullWidth variant="secondary" onClick={handleCancel}>
                    {args.cancelLabel ?? "Cancel"}
                </StriimButton>
            </Box>
        </Box>
    );
};
